/**
 * polylineWidthBand — the ONE SPAN WALK of a polyline: how a bulged span is
 * subdivided, and how a per-span quantity that varies ALONG the span (the
 * group 40→41 width taper) is carried onto each piece.
 *
 * # The three modules of a wide polyline, and what each owns
 *
 *   - **which width** — `drawing-model`'s `polylineWidth.ts`
 *     (`lwpolylineWidths` / `heavyPolylineWidths` /
 *     `hasPolylineWidth`): pure DXF group-code semantics (40 / 41 per
 *     vertex over 43 or the entity-level defaults), sited in the package so
 *     the adapters, the plot, `cad-core` and the bounds walk read one rule.
 *   - **where the spans go** — THIS module: the span walk, the bulged-span
 *     subdivision through `bulgeArc` / `bulgeArcPointAt`, and the taper
 *     interpolated onto each sub-segment.
 *   - **the band's offset math** — `./polylineWidthOutline.ts`: the two offset
 *     edges, the mitred joints, the square caps. Deliberately ignorant of DXF;
 *     it is handed already-resolved widths on already-straight station pairs.
 *   - **which FRAME all of that happens in** — `./polylinePlane.ts`: the exact
 *     inverse maps between the scene's WCS and the frame the width is authored
 *     in, whether that is an OCS-planar entity's own plane or the general
 *     linear map a non-similarity block INSERT applied on top of it.
 *     {@link widePolylineBandLoops} composes the three, and is the ONE
 *     entry point both the canvas batcher and the vector plot call for a
 *     finished band.
 *
 * # Why the span walk lives HERE
 *
 * Its consumers are on opposite sides of the app: `scene3d/batchedLayer.ts`
 * (the gl.LINES batcher, for BOTH the plain bulged centreline and the band
 * silhouette), the pick / snap pair that has to agree with it
 * (`scene3d/raycast/distance.ts`, `scene3d/snap/osnapIntersections.ts`,
 * `scene3d/snap/osnapCandidates.ts`) and `print/pdf/converters/
 * widePolylineBand.ts` (the vector plot's filled band). Until this module was
 * the single walk, the batcher had a private `tessellatePolylineSpans` and
 * this file had its own copy of the same rule: two subdivisions of the same
 * arc, deliberately given the same constant so they would agree, with a parity
 * test standing in for the agreement. `print/pdf` ALREADY depends on
 * `scene3d/adapters/geom` for the offset math it must share, so siting the
 * walk beside that math adds no edge and inverts no layering — whereas
 * hoisting it into a package would drag the renderer's `Vec3` and the outline
 * module's {@link WidthSegment} with it and split the pair.
 *
 * # Why the band is sampled rather than offset as arcs
 *
 * The offset of a circular arc by a CONSTANT width is a concentric arc, but
 * the offset of a TAPERED one is not a circular arc at all. Emitting the
 * constant case as exact arcs and the tapered case as something else would be
 * a SECOND offset implementation free to drift from the one in
 * `polylineWidthOutline.ts`. So a bulged span is subdivided FIRST — here — and
 * the offset math sees only straight station pairs, exactly as its own header
 * describes. The sample count is {@link BAND_ARC_SAMPLES_PER_TURN} per full
 * revolution, proportional to the swept angle, which is the same chord
 * tolerance the editor tessellates a bulged centreline at — necessarily so,
 * since it IS that tessellation: the plot's band edges are an offset of the
 * SAME polyline the screen draws, and the two cannot disagree about where the
 * band goes.
 *
 * The arc math itself is NOT re-derived (`bulgeArc` / `bulgeArcPointAt` —
 * `drawing-model`'s one sign-sensitive truth, shared with the bounds walk, the
 * grips and the exporter's bezier path).
 */

import {
  bulgeArc,
  bulgeArcPointAt,
  type PolylineWidth,
  type Vec3,
} from "@dxf-ai-editor/drawing-model";

import type { Aabb } from "./common";
import { boundsFromPoints, unionBounds } from "./common";
import {
  linearPolylinePlane,
  polylinePlane,
  type PolylineBandFrame,
} from "./polylinePlane";
import {
  widePolylineOutlineLoops,
  type WidthSegment,
} from "./polylineWidthOutline";

/**
 * Straight sub-segments a FULL revolution of a bulged span is sampled at. The
 * count actually used is proportional to the span's swept angle, so a barely
 * bent span costs one sub-segment and a full circle costs this many.
 *
 * This is the renderer's baseline tessellation count: `batchedLayer.ts` samples
 * a CIRCLE at the same number, so a bulge span sweeping a full 2*pi lands on
 * the same chord error a circle of that radius does.
 */
export const BAND_ARC_SAMPLES_PER_TURN = 64;

/**
 * One tessellated sub-segment of one polyline SPAN, carrying where it sits in
 * that span (`ua`/`ub` in 0..1) so a per-span quantity that varies along the
 * span — the group 40→41 width TAPER — can be interpolated onto it.
 *
 * A straight span is exactly one sub-segment (`ua` 0, `ub` 1); a bulged span
 * is `steps` of them along its arc.
 */
export interface PolylineSubSegment {
  a: Vec3;
  b: Vec3;
  /** Index of the span (== the index of its starting vertex). */
  span: number;
  ua: number;
  ub: number;
}

/** Sub-segment count for a span sweeping `signedSweep` radians. */
function arcSampleCount(signedSweep: number): number {
  const turns = Math.abs(signedSweep) / (Math.PI * 2);
  return Math.max(1, Math.ceil(BAND_ARC_SAMPLES_PER_TURN * turns));
}

/**
 * Walk a polyline's spans, expanding each BULGED one into its arc samples.
 *
 * This is THE tessellation of the centreline. {@link polylineCentrelineSamples}
 * projects it to a point run for the plain stroke — the run `batchedLayer.ts`,
 * the pick (`raycast/distance.ts`) and the INTERSECTION snap
 * (`snap/osnapIntersections.ts`) all read, so no two of them can follow a
 * different curve; {@link polylineBandSegments} pairs it with the per-vertex
 * taper to build the band both the canvas and the vector plot draw.
 *
 * A closed run's wrap span (last vertex → first) is included, which is why the
 * span count is `points.length` when `closed`. The span's own END VERTEX is
 * emitted verbatim as its last sample — never a re-evaluation of the arc at
 * u = 1 — so the chain stays exactly connected and the next span starts where
 * this one ended. Interior samples take the arc's XY and interpolate Z, since
 * a bulge is a 2D-polyline field (Autodesk DXF reference, group 42): a span
 * whose endpoints were lifted to different Z (a block child spanning an
 * elevation step) stays straight in Z.
 */
export function polylineSpanSubSegments(
  points: readonly Vec3[],
  closed: boolean,
  bulges: readonly number[] | undefined,
): PolylineSubSegment[] {
  const out: PolylineSubSegment[] = [];
  if (points.length < 2) return out;
  const spanCount = closed ? points.length : points.length - 1;
  for (let span = 0; span < spanCount; span += 1) {
    const from = points[span];
    const to = points[(span + 1) % points.length];
    if (!from || !to) continue;
    const arc = bulgeArc(from, to, bulges?.[span] ?? 0);
    if (!arc) {
      out.push({ a: from, b: to, span, ua: 0, ub: 1 });
      continue;
    }
    const steps = arcSampleCount(arc.signedSweep);
    let tail = from;
    for (let step = 1; step <= steps; step += 1) {
      const u = step / steps;
      let head: Vec3;
      if (step === steps) {
        head = to;
      } else {
        const xy = bulgeArcPointAt(arc, u);
        head = { x: xy.x, y: xy.y, z: from.z + (to.z - from.z) * u };
      }
      out.push({ a: tail, b: head, span, ua: (step - 1) / steps, ub: u });
      tail = head;
    }
  }
  return out;
}

/**
 * THE drawn CENTRELINE of a polyline: its vertices with every BULGED span
 * replaced by the samples of the arc that span really is — evaluated in the
 * frame the arc is authored in.
 *
 * This is the centreline half of the sentence {@link widePolylineBandLoops}
 * already says for the band: the span walk is 2D and it is only correct in the
 * frame the 2D construction belongs to. A bulge (DXF group 42) is a
 * 2D-polyline field measured in the entity's OWN OCS plane, and
 * `heavyPolylineWcsResolver` has already lifted the vertices OUT of that plane
 * by the time the renderer sees them. An OCS lift maps the chord correctly but
 * NOT the bow: for extrusion `(0, 1, 0)` the OCS y axis IS world Z, so the
 * 5-unit bow of a `(0,0) → (10,0)` bulge-1 semicircle belongs on world Z and
 * evaluating `bulgeArc` on the lifted points drew it on world Y instead — a
 * gross, ordinary-geometry error that `computeEntityWcsBounds` (which builds
 * the arc in OCS-local coordinates and lifts it, `wcsBoundsLeaf.ts`) never
 * made, so the model box and the canvas disagreed by the whole bow.
 *
 * So the order here is the SAME one `widePolylineBandLoops` uses — project
 * into the plane, run the unchanged {@link polylineSpanSubSegments} walk
 * there, lift the finished samples — and it is the SAME walk, so the band
 * edges and the centreline can never follow two different subdivisions of one
 * arc.
 *
 * A run with no bulge at all (or fewer than two points) returns the input
 * array UNTOUCHED — no projection, no re-composition, not one coordinate
 * perturbed by the round trip — so every straight polyline, and every polyline
 * in the WCS XY plane, keeps the byte-identical samples it had before.
 */
export function polylineCentrelineSamples(
  points: readonly Vec3[],
  closed: boolean,
  bulges: readonly number[] | undefined,
  extrusion?: Vec3,
): readonly Vec3[] {
  if (!bulges || points.length < 2) return points;
  let anyBulge = false;
  for (const b of bulges) {
    if (b !== 0) {
      anyBulge = true;
      break;
    }
  }
  if (!anyBulge) return points;

  const plane = polylinePlane(extrusion);
  const local = plane ? points.map((p) => plane.toLocal(p)) : points;
  const segments = polylineSpanSubSegments(local, closed, bulges);
  if (segments.length === 0) return points;

  // A closed run's wrap edge is drawn by the caller's implicit last→first
  // link, and that edge IS the final sub-segment (its `b` is `points[0]`); an
  // open run has to carry its own final point.
  const out: Vec3[] = segments.map((seg) => seg.a);
  if (!closed) out.push(segments[segments.length - 1]!.b);
  return plane ? out.map((p) => plane.toWorld(p)) : out;
}

/**
 * The group 40 → 41 taper of one span, evaluated at parameter `u` in 0..1.
 *
 * The two ENDS are the authored values verbatim: a span's width at its start
 * vertex IS its group 40 and at its end vertex IS its group 41, so `u` 0 and 1
 * return them exactly rather than through a lerp that can land one ulp away.
 * A span with no width pair at all is width 0 (the caller then strokes the
 * centreline instead of drawing a band).
 */
function taperAt(width: PolylineWidth | undefined, u: number): number {
  if (!width) return 0;
  if (u === 0) return width.start;
  if (u === 1) return width.end;
  return width.start + (width.end - width.start) * u;
}

/**
 * The polyline's spans as the straight, width-carrying station pairs
 * {@link WidthSegment} that `widePolylineOutlineLoops` consumes.
 *
 * `widths` is per-VERTEX and indexed by the span's STARTING vertex, so a
 * closed polyline's last entry describes its wrap span (Autodesk DXF
 * reference). A bulged span is subdivided along its arc by
 * {@link polylineSpanSubSegments} with its taper interpolated onto each piece;
 * a straight span is one pair carrying the vertex's 40/41 verbatim.
 */
export function polylineBandSegments(
  points: readonly Vec3[],
  closed: boolean,
  bulges: readonly number[] | undefined,
  widths: readonly PolylineWidth[],
): WidthSegment[] {
  return polylineSpanSubSegments(points, closed, bulges).map((seg) => {
    const pair = widths[seg.span];
    return {
      a: seg.a,
      b: seg.b,
      wa: taperAt(pair, seg.ua),
      wb: taperAt(pair, seg.ub),
    };
  });
}

/**
 * A wide polyline's finished band SILHOUETTE — the ONE call that turns points,
 * bulges and resolved widths into the closed loops both the canvas
 * (`batchedLayer.ts`, stroked as gl.LINES rings) and the vector plot
 * (`print/pdf/converters/widePolylineBand.ts`, filled) draw.
 *
 * It exists as one function because the three steps have an ORDER that is part
 * of the geometry, not an implementation detail: the span walk and the offset
 * math are both 2D, and they are only correct in the FRAME THE WIDTH IS
 * AUTHORED IN. Whenever the caller's `points` have already been mapped OUT of
 * that frame, they are projected back into it, constructed there, and the
 * finished loops mapped forward again. Leaving the order to each caller is
 * exactly how the two sides came to disagree — twice:
 *
 *   - `extrusion` (DXF group 210) — the points were lifted out of the entity's
 *     OCS plane, and an OCS lift does not preserve a transverse direction
 *     (@codex round 5, FINDING B — `./polylinePlane.ts`);
 *   - `bandFrame` — the points were mapped by a general LINEAR map, today the
 *     affine of a NON-SIMILARITY block INSERT, under which a width is not a
 *     scalar at all (@codex round 6 — `../typed/blockChildBandFrame.ts`).
 *
 * `bandFrame` SUPERSEDES `extrusion` when both are supplied: it already
 * composes the entity's own OCS basis into itself, so applying the two in
 * sequence would lift the same plane twice. The plot passes NEITHER — its
 * points never left the authored frame in the first place and its own affine
 * carries both maps afterwards, which is why it was the side that was right
 * both times.
 *
 * With no frame at all — every LWPOLYLINE the canvas draws today, every
 * untilted POLYLINE, and every block child under a SIMILARITY insert (where
 * `width · |scale|` is the exact image and the clone still carries it) — this
 * short-circuits to the plain 2D path, byte-identical to the direct
 * `widePolylineOutlineLoops(polylineBandSegments(...))` pair this replaces.
 */
export function widePolylineBandLoops(
  points: readonly Vec3[],
  closed: boolean,
  bulges: readonly number[] | undefined,
  widths: readonly PolylineWidth[],
  extrusion?: Vec3,
  bandFrame?: PolylineBandFrame,
): Vec3[][] {
  const plane = bandFrame
    ? linearPolylinePlane(bandFrame)
    : polylinePlane(extrusion);
  if (!plane) {
    return widePolylineOutlineLoops(
      polylineBandSegments(points, closed, bulges, widths),
      closed,
    );
  }
  const local = points.map((p) => plane.toLocal(p));
  const loops = widePolylineOutlineLoops(
    polylineBandSegments(local, closed, bulges, widths),
    closed,
  );
  return loops.map((loop) => loop.map((p) => plane.toWorld(p)));
}

/**
 * The AABB of the loops {@link widePolylineBandLoops} returns — the box of the
 * band a wide polyline is ACTUALLY DRAWN as, mitred outer corners included.
 *
 * `null` when there is no band at all (every span degenerate, or no width): the
 * caller then keeps whatever centreline box it already has.
 *
 * # Why the box comes from the LOOPS and not from a half-width pad
 *
 * A polyline's drawn silhouette reaches further than `centreline ± w/2`: at a
 * sharp joint whose miter stays under `polylineWidthOutline.ts`' `MITER_LIMIT` the offset edges
 * intersect out along the bisector, and that spike is part of what the canvas
 * strokes (`polylineWidthOutline.ts::miterJoint` accepts any `|t| <= 4 ·
 * halfWidth`, so the corner can sit up to FOUR half-widths past the vertex).
 * Width 2 on `(0,0) → (10,0) → (1.34,5)`:
 *
 *     centreline hull ⊕ one half-width   maxX = 11
 *     generated band loop                maxX = 13.73195599951599
 *
 * — over two and a half units of DRAWN geometry outside a box padded by the
 * half width, which a spatial-index prefilter may then cull (@codex PR #785
 * round 11, F4). Deriving the box from the generated loops is EXACT rather
 * than conservative, and needs no second copy of the miter rule: it is the
 * same call the batcher and the vector plot stroke.
 */
export function widePolylineBandBounds(
  points: readonly Vec3[],
  closed: boolean,
  bulges: readonly number[] | undefined,
  widths: readonly PolylineWidth[],
  extrusion?: Vec3,
  bandFrame?: PolylineBandFrame,
): Aabb | null {
  let box: Aabb | null = null;
  for (const loop of widePolylineBandLoops(
    points,
    closed,
    bulges,
    widths,
    extrusion,
    bandFrame,
  )) {
    // DOCUMENTED EQUIVALENT MUTANT (targeted mutation testing) — the twin of
    // `./heavyPolylineDrawnBounds.ts`' `if (box)`:
    //   file:line       this file, the `if (b)` guard
    //   mutator         ConditionalExpression → `true`
    //   why equivalent  `boundsFromPoints` returns `null` only for an EMPTY
    //                   point list, and `widePolylineOutlineLoops` never emits
    //                   an empty loop: it returns `[]` outright when
    //                   `dropDegenerateSegments` leaves nothing, and every loop
    //                   it does emit has at least the two butt corners
    //                   `offsetSide` pushes for a live segment.
    //   the invariant   every loop of `widePolylineBandLoops` is non-empty
    //   proved by       `./heavyPolylineSelectDrawnShape.test.ts`' "reports NO
    //                   bounds at all for a polyline with no vertices" and
    //                   `./lwpolylineDrawnBounds.test.ts`' "falls back to the
    //                   model box when there is no band to build", which take
    //                   the `[]` path rather than the empty-loop one
    //   kept because    a direct change to `widePolylineOutlineLoops` must not
    //                   be able to poison the box with `undefined`
    const b = boundsFromPoints(loop);
    if (b) box = box ? unionBounds(box, b) : b;
  }
  return box;
}
